import React, { useCallback } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import { FaCircleExclamation } from "react-icons/fa6"
import { useQueryClient } from "@tanstack/react-query"
import useLogging from "../../hooks/useLogging"

// Goal `value` can be either a string (single value) or a dict keyed by
// day-of-week index 0..6 (Monday..Sunday) — e.g. `targetPatientVolumes` is a
// per-day map. Treat anything that isn't a string as the per-day shape.
const DAY_LABELS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]
const DAY_SHORT_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
const DAY_KEYS = ["0", "1", "2", "3", "4", "5", "6"]

const isPerDayGoalValue = value =>
	value !== null && typeof value === "object" && !Array.isArray(value)

// Tailwind class fragments used in both read-only and editable input renders.
const INPUT_BASE =
	"block w-full rounded-md border-0 py-1.5 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 shadow-sm"
const INPUT_OK = "ring-1 ring-inset ring-gray-300 focus:ring-indigo-500"
const INPUT_ERROR =
	"pr-9 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-red-500"
const READ_ONLY_BOX =
	"block w-full rounded-md bg-gray-50 ring-1 ring-inset ring-gray-200 py-1.5 px-2 text-gray-900 sm:text-sm shadow-sm"

function Goals({
	hasWriteSettingsPermission,
	settingsData,
	formData,
	setFormData,
	errors,
	setErrors,
	setNotification
}) {
	const { getAccessTokenSilently } = useAuth0()
	const queryClient = useQueryClient()
	const logging = useLogging()

	const handleGoalSubmit = useCallback(
		async e => {
			e.preventDefault()

			try {
				const token = await getAccessTokenSilently({
					audience: process.env.REACT_APP_API_IDENTIFIER
				})

				const normalizeValue = value =>
					isPerDayGoalValue(value)
						? Object.fromEntries(DAY_KEYS.map(key => [key, value[key].trim()]))
						: value.trim()

				const goals = settingsData.goals.map((goal, index) => ({
					id: goal.id,
					name: goal.name,
					value: normalizeValue(formData.goals[index].value)
				}))

				const validationErrors = goals.reduce((acc, goal, index) => {
					if (isPerDayGoalValue(goal.value)) {
						if (DAY_KEYS.some(key => !goal.value[key])) {
							acc[`goals.${index}.value`] = "All weekday values are required"
						}
					} else if (!goal.value) {
						acc[`goals.${index}.value`] = "Goal value cannot be empty"
					}
					return acc
				}, {})

				if (Object.keys(validationErrors).length > 0) {
					logging.warn("Validation errors found:", { errors: validationErrors })
					setErrors(validationErrors)
					return
				}

				const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/goals`, {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${token}`
					},
					body: JSON.stringify(goals)
				})

				if (!response.ok) {
					throw new Error("Failed to update goals")
				}

				await response.json()
				queryClient.invalidateQueries({ queryKey: ["settings"] })
				setNotification(prev => ({
					...prev,
					visible: true,
					message: "Goals Successfully Updated",
					success: true
				}))
				setTimeout(
					() => setNotification(prev => ({ ...prev, visible: false, message: "" })),
					3000
				)
			} catch (error) {
				logging.error("Error updating goals:", {
					error: error.message,
					stack: error.stack
				})
				setNotification(prev => ({
					...prev,
					visible: true,
					message: "Error updating goals",
					success: false
				}))
				setTimeout(
					() => setNotification(prev => ({ ...prev, visible: false, message: "" })),
					3000
				)
			}
		},
		[
			getAccessTokenSilently,
			queryClient,
			formData,
			settingsData,
			setErrors,
			setNotification,
			logging
		]
	)

	// --- Single-value goal renderers ---

	const renderSingleInput = (goal, index) => {
		const hasError = Boolean(errors.goals?.[index]?.value)
		return (
			<div className="relative">
				<input
					type="text"
					value={formData.goals[index].value}
					onChange={e => {
						const newGoals = [...formData.goals]
						newGoals[index] = { ...newGoals[index], value: e.target.value }
						setFormData(prev => ({ ...prev, goals: newGoals }))
					}}
					id={`goals.${index}.value`}
					placeholder="Enter goal value"
					className={`${INPUT_BASE} ${hasError ? INPUT_ERROR : INPUT_OK}`}
					aria-invalid={hasError ? "true" : "false"}
				/>
				{hasError && (
					<div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
						<FaCircleExclamation aria-hidden="true" className="h-5 w-5 text-red-500" />
					</div>
				)}
			</div>
		)
	}

	const renderSingleReadOnly = (goal, index) => (
		<p id={`goals.${index}.value`} className={READ_ONLY_BOX}>
			{goal.value || "N/A"}
		</p>
	)

	// --- Per-day goal renderers ---

	const renderPerDayInputs = (goal, index) => {
		const currentValue = formData.goals[index].value
		return (
			<div className="mt-3 rounded-md border border-gray-200 bg-gray-50/40 p-4">
				<div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
					{DAY_KEYS.map((key, dayIndex) => (
						<div key={key} className="flex flex-col">
							<label
								htmlFor={`goals.${index}.value.${key}`}
								className="text-xs font-medium text-gray-600 mb-1"
							>
								<span className="hidden lg:inline">{DAY_SHORT_LABELS[dayIndex]}</span>
								<span className="lg:hidden">{DAY_LABELS[dayIndex]}</span>
							</label>
							<input
								type="text"
								id={`goals.${index}.value.${key}`}
								value={currentValue[key]}
								onChange={e => {
									const newGoals = [...formData.goals]
									newGoals[index] = {
										...newGoals[index],
										value: { ...newGoals[index].value, [key]: e.target.value }
									}
									setFormData(prev => ({ ...prev, goals: newGoals }))
								}}
								className={`${INPUT_BASE} ${INPUT_OK} text-center`}
							/>
						</div>
					))}
				</div>
			</div>
		)
	}

	const renderPerDayReadOnly = goal => (
		<div className="mt-3 rounded-md border border-gray-200 bg-gray-50/40 p-4">
			<div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
				{DAY_KEYS.map((key, dayIndex) => (
					<div key={key} className="flex flex-col">
						<span className="text-xs font-medium text-gray-600 mb-1">
							<span className="hidden lg:inline">{DAY_SHORT_LABELS[dayIndex]}</span>
							<span className="lg:hidden">{DAY_LABELS[dayIndex]}</span>
						</span>
						<p className={`${READ_ONLY_BOX} text-center`}>{goal.value[key]}</p>
					</div>
				))}
			</div>
		</div>
	)

	// --- Top-level renderer ---

	const renderGoalRow = (goal, index) => {
		const perDay = isPerDayGoalValue(goal.value)
		const error = errors.goals?.[index]?.value
		return (
			<div
				key={index}
				className={`${perDay ? "sm:col-span-3" : "sm:col-span-1"} col-span-1`}
			>
				<label
					htmlFor={`goals.${index}.value`}
					className="block text-sm font-medium text-gray-800"
				>
					{goal.name}
				</label>
				{perDay
					? hasWriteSettingsPermission
						? renderPerDayInputs(goal, index)
						: renderPerDayReadOnly(goal)
					: hasWriteSettingsPermission
						? renderSingleInput(goal, index)
						: renderSingleReadOnly(goal, index)}
				{error && (
					<p id={`goals.${index}.value-error`} className="mt-1.5 text-sm text-red-600">
						{error}
					</p>
				)}
			</div>
		)
	}

	const header = (
		<div className="mb-6">
			<h2 className="text-2xl font-semibold text-gray-900">Optimizer Goals</h2>
			<p className="mt-1 text-sm text-gray-600">
				Target metrics the scheduling optimizer is calibrated against. Edit values to
				adjust how the optimizer balances staffing, utilization, and patient throughput.
			</p>
		</div>
	)

	return (
		<div>
			{header}
			{hasWriteSettingsPermission ? (
				<form onSubmit={handleGoalSubmit}>
					<div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-5">
						{settingsData.goals.map((goal, index) => renderGoalRow(goal, index))}
					</div>
					<div className="mt-8 flex justify-end">
						<button
							type="submit"
							className="rounded-md bg-customBlue px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#1C2675] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#222E93]"
						>
							Update Goals
						</button>
					</div>
				</form>
			) : (
				<div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-5">
					{settingsData.goals.map((goal, index) => renderGoalRow(goal, index))}
				</div>
			)}
		</div>
	)
}

export default Goals
